import React, { useMemo, useState } from 'react';
import {
  IonBadge,
  IonButton,
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSearchbar,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { useQuery } from '@tanstack/react-query';
import { IRawMaterial } from 'shared';
import apiClient from '../api/client';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import UserMenu from '../components/UserMenu';
import { formatCurrency, formatDecimal } from '../utils/format';
import { RefresherEvent } from '../types/ionic';
import MetricCard from '../components/MetricCard';
import { useHistory } from 'react-router-dom';

const Materials: React.FC = () => {
  const { t, language } = useLanguage();
  const locale = language === 'vi' ? 'vi-VN' : 'en-US';
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'low' | 'ok' | 'empty'>('all');
  const history = useHistory();

  const materialsQuery = useQuery({
    queryKey: ['materials', 'list'],
    queryFn: async () => {
      const { data } = await apiClient.get<IRawMaterial[]>('/materials');
      return data;
    },
  });

  const handleRefresh = async (event: RefresherEvent) => {
    try {
      await materialsQuery.refetch();
    } finally {
      event.detail.complete();
    }
  };

  const items = materialsQuery.data ?? [];

  const formatQuantity = (value: number) =>
    formatDecimal(Number(value || 0), {
      maximumFractionDigits: 6,
    });

  const formatVND = (value: number) => formatCurrency(Number(value || 0), locale);

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const stats = useMemo(() => {
    const totalStockValue = items.reduce(
      (sum, material) =>
        sum + Number(material.currentStock || 0) * Number(material.costPerUnit || 0),
      0,
    );
    const lowStockCount = items.filter(
      (material) => Number(material.currentStock || 0) < Number(material.minStockLevel || 0),
    ).length;
    const totalItems = items.length;
    return {
      totalStockValue,
      lowStockCount,
      totalItems,
    };
  }, [items]);

  const filteredItems = useMemo(() => {
    return items
      .filter((material) => {
        if (!normalizedSearch) {
          return true;
        }
        return material.name.toLowerCase().includes(normalizedSearch);
      })
      .filter((material) => {
        const currentStock = Number(material.currentStock || 0);
        const minStock = Number(material.minStockLevel || 0);
        if (statusFilter === 'all') {
          return true;
        }
        if (statusFilter === 'low') {
          return currentStock < minStock;
        }
        if (statusFilter === 'ok') {
          return currentStock >= minStock;
        }
        // empty
        return currentStock <= 0;
      });
  }, [items, normalizedSearch, statusFilter]);

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonTitle>{t('materials')}</IonTitle>
          <LanguageToggle />
          <UserMenu />
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent pullingText="Pull to refresh" />
        </IonRefresher>

        <div style={{ paddingTop: 'var(--ion-safe-area-top)' }}>
          <IonButton expand="block" style={{ marginBottom: 16 }} onClick={() => history.push('/tabs/materials/new')}>
            {t('addMaterial') ?? 'Add material'}
          </IonButton>
          <IonSearchbar
            value={searchTerm}
            onIonChange={(event) => setSearchTerm(event.detail.value ?? '')}
            placeholder={t('search')}
          />

          <IonSegment
            value={statusFilter}
            onIonChange={(event) => {
              const value = (event.detail.value as typeof statusFilter | undefined) ?? 'all';
              setStatusFilter(value);
            }}
            style={{ marginTop: 12 }}
          >
            <IonSegmentButton value="all">
              <IonLabel>{t('all')}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="low">
              <IonLabel>{t('lowStock')}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="ok">
              <IonLabel>{t('inStock')}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="empty">
              <IonLabel>{t('empty') ?? 'Empty'}</IonLabel>
            </IonSegmentButton>
          </IonSegment>

          <section style={{ marginTop: 24 }}>
            <MetricCard
              title={t('inventory')}
              value={formatVND(stats.totalStockValue)}
              context={`${stats.totalItems} ${t('materials').toLowerCase()}`}
              accent="primary"
            />
            <MetricCard
              title={t('lowStockAlerts')}
              value={`${stats.lowStockCount}`}
              context={t('lowStock')}
              accent="warning"
            />
          </section>

          {materialsQuery.isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
              <IonSpinner />
            </div>
          ) : filteredItems.length === 0 ? (
            <IonText color="medium">
              <p>{t('noData')}</p>
            </IonText>
          ) : (
            <IonList>
              {filteredItems.map((material) => {
                const currentStock = Number(material.currentStock || 0);
                const minStock = Number(material.minStockLevel || 0);
                const isLowStock = currentStock < minStock;

                return (
                  <IonItem key={material.id} routerLink={`/tabs/materials/${material.id}`} detail>
                    <IonLabel>
                      <h3 style={{ fontWeight: 600 }}>{material.name}</h3>
                      <p>
                        {t('currentStock')}: {formatQuantity(currentStock)} {material.unit}
                      </p>
                      <p>
                        {t('minStockLevel')}: {formatQuantity(minStock)} {material.unit}
                      </p>
                      <p>
                        {t('costPerUnit')}: {formatVND(Number(material.costPerUnit || 0))}
                      </p>
                    </IonLabel>
                    {isLowStock ? (
                      <IonBadge color="danger" slot="end">
                        {t('lowStock')}
                      </IonBadge>
                    ) : (
                      <IonBadge color="success" slot="end">
                        {t('inStock')}
                      </IonBadge>
                    )}
                  </IonItem>
                );
              })}
            </IonList>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Materials;
